import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { errText, fmt, PasswordInput } from "@/components/control/kit";
import { PublicShell, publicInput, publicButton, publicLabel } from "@/components/control/PublicShell";

export default function AcceptInvitation() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("checking");
  const [reason, setReason] = useState("");
  const [pw, setPw] = useState("");
  const [rules, setRules] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); setReason("Diese Einladung ist nicht gültig."); return; }
    api.get(`/auth/invitation?token=${encodeURIComponent(token)}`)
      .then((r) => { setInfo(r.data); setState("form"); })
      .catch((err) => { setState("invalid"); setReason(errText(err)); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 10) { setError("Das Passwort muss mindestens 10 Zeichen lang sein."); return; }
    if (!rules) { setError("Bitte die Nutzungsregeln bestätigen."); return; }
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/invitation/accept", { token, password: pw, accept_rules: rules });
      setState("done");
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicShell title="Teameinladung annehmen" subtitle="Prüfe deine zugewiesene interne Rolle vor Annahme der Einladung. Den zweiten Faktor richtest du bei der ersten Anmeldung ein." testId="accept-invitation-page">
      {state === "checking" && <Loader2 className="h-6 w-6 animate-spin text-brand-magenta" aria-label="Einladung wird geprüft" />}
      {state === "invalid" && (
        <p role="alert" data-testid="invitation-invalid" className="flex gap-2 rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-orange-800">
          <Clock className="h-5 w-5 shrink-0" aria-hidden="true" /> {reason} Bitte wende dich an die Person, die dich eingeladen hat.
        </p>
      )}
      {state === "done" && (
        <p role="status" data-testid="invitation-done" className="flex gap-2 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
          <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" /> Dein Konto ist angelegt. Melde dich jetzt an und richte die Authenticator-App ein.
        </p>
      )}
      {state === "form" && info && (
        <form onSubmit={submit} className="space-y-4" noValidate>
          <dl className="space-y-2.5" data-testid="invitation-info">
            {[["Einladung", `${info.first_name} (${info.email})`], ["Vorgesehene Rolle", info.role_label], ["Umfang", info.scope],
              ["Gültig bis", fmt(info.expires_at)], ["Private Passfelder", "Nicht in der Rolle enthalten"]].map(([k, v]) => (
              <div key={k} className="rounded-xl border border-[#E6E1F0] bg-white px-4 py-3">
                <dt className="text-sm font-semibold text-zinc-500">{k}</dt><dd className="mt-1 text-base text-brand-purple">{v}</dd>
              </div>
            ))}
          </dl>
          <label htmlFor="inv-pw" className={publicLabel}>Passwort (mind. 10 Zeichen)</label>
          <PasswordInput id="inv-pw" autoComplete="new-password" value={pw} onChange={setPw} testId="invitation-password" className={publicInput} strength />
          <label htmlFor="inv-rules" className="flex items-start gap-2 text-sm text-zinc-800">
            <input id="inv-rules" type="checkbox" checked={rules} onChange={(e) => setRules(e.target.checked)} data-testid="invitation-rules" className="mt-0.5 h-4 w-4 accent-[#D50B58]" />
            Ich bestätige die internen Nutzungs- und Datenschutzregeln von Control OS.
          </label>
          {error && <p role="alert" data-testid="invitation-error" className="text-sm text-red-700">{error}</p>}
          <button type="submit" disabled={busy} data-testid="invitation-submit" className={publicButton}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />} Einladung annehmen
          </button>
        </form>
      )}
    </PublicShell>
  );
}
